import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle, Bug, CreditCard, Lock, BookOpen, ShieldOff, MessageSquareWarning,
  Image as ImageIcon, X, Send, CheckCircle2, Loader2, Trash2,
} from "lucide-react";
import { API, formatApiError } from "../lms/contexts/AuthContext";

/* Categories mirror /app/backend/routes/reports.py.
   Keep the keys synced or the API will reject submissions. */
const GENERAL_CATEGORIES = [
  { key: "bug",     label: "Bug / something broken",  icon: Bug,        accent: "#dc2626" },
  { key: "account", label: "Account / login problem", icon: Lock,       accent: "#db2777" },
  { key: "payment", label: "Payment / billing",       icon: CreditCard, accent: "#d97706" },
  { key: "content", label: "Lecture or notes problem", icon: BookOpen,   accent: "#0284c7" },
  { key: "access",  label: "Can't access something I paid for", icon: ShieldOff, accent: "#7c3aed" },
  { key: "other",   label: "Other",                   icon: MessageSquareWarning, accent: "#475569" },
];

const MCQ_CATEGORIES = [
  { key: "mcq_wrong",       label: "Answer marked is wrong",        icon: AlertTriangle, accent: "#dc2626" },
  { key: "mcq_unclear",     label: "Question is unclear / ambiguous", icon: MessageSquareWarning, accent: "#d97706" },
  { key: "mcq_typo",        label: "Typo / formatting issue",        icon: Bug,           accent: "#db2777" },
  { key: "mcq_image",       label: "Image missing or broken",        icon: ImageIcon,     accent: "#7c3aed" },
  { key: "mcq_explanation", label: "Explanation is incorrect / missing", icon: BookOpen, accent: "#0284c7" },
  { key: "mcq_other",       label: "Other MCQ issue",                icon: MessageSquareWarning, accent: "#475569" },
];

const MAX_ATTACHMENTS = 3;
const MAX_BYTES = 4 * 1024 * 1024;

/* ───────── ReportIssueDialog ─────────
   Light-theme modal that visually matches the student portal. Renders
   into a portal so it stacks above the AnimatedOutlet and the bottom dock.
   Two key fixes vs the previous version:
     1. After submit, the scrollable body is reset to scrollTop=0 so the
        success state is actually visible on desktop (previously it would
        render off-screen if the user had scrolled down through the form).
     2. Submit button is gated only by "no category yet" — title/description
        validation is enforced inline so the button doesn't sit silently
        disabled with no explanation. */
export default function ReportIssueDialog({ open, onClose, kind = "general", mcqId, mcqPreview, onSubmitted }) {
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const bodyRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setCategory(""); setTitle(""); setDescription("");
      setAttachments([]); setErr(""); setDone(null); setSubmitting(false);
    }
  }, [open, kind, mcqId]);

  // Iter 193.9 — lock body scroll while the modal is open so scrolling
  // inside the modal doesn't bleed through to the page behind. Preserves
  // the current scroll position and restores it on close so the student
  // lands exactly where they were.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // CRITICAL: when transitioning to the success state, scroll the body
  // to the top — otherwise on desktop with a tall form, the user's
  // scrollTop persists and the (shorter) success card renders above
  // their viewport, making the page look unresponsive.
  useEffect(() => {
    if (done && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [done]);

  const categories = kind === "mcq" ? MCQ_CATEGORIES : GENERAL_CATEGORIES;

  const onFiles = useCallback(async (fileList) => {
    setErr("");
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const fresh = [];
    for (const f of Array.from(fileList).slice(0, remaining)) {
      if (!f.type.startsWith("image/")) { setErr(`"${f.name}" isn't an image.`); continue; }
      if (f.size > MAX_BYTES) { setErr(`"${f.name}" is over the 4 MB limit.`); continue; }
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result); r.onerror = rej;
        r.readAsDataURL(f);
      });
      fresh.push({ id: `${f.name}-${f.size}-${Date.now()}`, dataUrl, name: f.name, size: f.size });
    }
    if (fresh.length > 0) setAttachments(prev => [...prev, ...fresh]);
  }, [attachments]);

  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const submit = async () => {
    setErr("");
    if (!category)                     return setErr("Please pick a category above.");
    if (title.trim().length < 3)       return setErr("Add a short title (at least 3 characters).");
    if (description.trim().length < 5) return setErr("Describe the issue with at least 5 characters.");
    setSubmitting(true);
    try {
      const payload = {
        kind, category,
        title: title.trim(),
        description: description.trim(),
        attachments: attachments.map(a => a.dataUrl),
        page_url: typeof window !== "undefined" ? window.location.href : null,
      };
      if (kind === "mcq") payload.mcq_id = mcqId;
      const { data } = await axios.post(`${API}/me/reports`, payload);
      setDone(data);
      onSubmitted && onSubmitted(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Couldn't submit the report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-end md:items-center justify-center"
          style={{ background: "rgba(11,11,13,0.55)", backdropFilter: "blur(4px)" }}
          onClick={onClose}
          data-testid="report-dialog"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-xl mx-auto flex flex-col"
            style={{
              background: "#ffffff",
              color: "#0b0b0d",
              borderTopLeftRadius: 22, borderTopRightRadius: 22,
              borderBottomLeftRadius: 22, borderBottomRightRadius: 22,
              border: "1px solid rgba(11,11,13,0.08)",
              boxShadow: "0 30px 80px -10px rgba(11,11,13,0.35)",
              maxHeight: "calc(100vh - 40px)",
              margin: "0 12px",
              overflow: "hidden",
            }}
          >
            {/* Mobile drag handle — subtle affordance for a bottom sheet */}
            <div className="flex justify-center py-2 md:hidden">
              <div
                className="w-10 h-1 rounded-full"
                style={{ background: "rgba(11,11,13,0.15)" }}
                aria-hidden
              />
            </div>

            {/* Header — colored kicker band so students immediately
                understand context (blue for general, red for MCQ). */}
            <div
              className="flex items-start justify-between gap-3 px-5 md:px-6 py-4"
              style={{
                borderBottom: "1px solid rgba(11,11,13,0.06)",
                background: kind === "mcq"
                  ? "linear-gradient(180deg, #fef2f2 0%, #ffffff 100%)"
                  : "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)",
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 mb-1">
                  <span
                    className="inline-flex items-center justify-center rounded-full"
                    style={{
                      width: 22, height: 22,
                      background: kind === "mcq" ? "#dc2626" : "#2563eb",
                      color: "#ffffff",
                    }}
                    aria-hidden
                  >
                    {kind === "mcq"
                      ? <AlertTriangle size={12} strokeWidth={3} />
                      : <MessageSquareWarning size={12} strokeWidth={3} />}
                  </span>
                  <span
                    className="font-mono text-[10.5px] uppercase tracking-[0.22em] font-bold"
                    style={{ color: kind === "mcq" ? "#b91c1c" : "#1d4ed8" }}
                  >
                    {kind === "mcq" ? "Report this MCQ" : "Report an issue"}
                  </span>
                </div>
                <h2 className="text-[19px] md:text-[22px] font-black leading-tight" style={{ color: "#0b0b0d" }}>
                  {done ? "Submitted — our team is on it" : "Tell us what went wrong"}
                </h2>
                {!done && (
                  <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
                    We read every report. Support usually replies within 24h.
                  </p>
                )}
              </div>
              <button onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0"
                style={{ background: "rgba(11,11,13,0.06)", color: "#475569" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(11,11,13,0.12)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(11,11,13,0.06)"}
                data-testid="report-close" aria-label="Close">
                <X size={15} />
              </button>
            </div>

            {/* Body (scrollable) */}
            <div
              ref={bodyRef}
              className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-5"
              style={{ minHeight: 0 }}
            >
              {done ? (
                <SubmissionConfirmation report={done} />
              ) : (
                <>
                  {kind === "mcq" && mcqPreview && <McqPreviewCard preview={mcqPreview} />}

                  <FieldGroup label="What kind of issue is this?" required>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="report-categories">
                      {categories.map((c) => {
                        const Icon = c.icon;
                        const active = category === c.key;
                        return (
                          <button
                            key={c.key} type="button"
                            onClick={() => setCategory(c.key)}
                            data-testid={`report-cat-${c.key}`}
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-[12px] text-[13px] text-left transition-all"
                            style={{
                              background: active ? "rgba(2,132,199,0.06)" : "#f8fafc",
                              border: `1.5px solid ${active ? "#0284c7" : "rgba(11,11,13,0.10)"}`,
                              color: active ? "#0b0b0d" : "#1e293b",
                              fontWeight: active ? 600 : 500,
                              boxShadow: active ? "0 0 0 3px rgba(2,132,199,0.10)" : "none",
                            }}
                          >
                            <Icon size={14} style={{ color: c.accent }} />
                            <span className="flex-1 leading-tight">{c.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </FieldGroup>

                  <FieldGroup label="One-line summary" required>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Can't open the Notes PDF for Lecture 12"
                      maxLength={140}
                      data-testid="report-title"
                      style={inputStyle}
                    />
                    <CharCount n={title.length} max={140} />
                  </FieldGroup>

                  <FieldGroup label="Describe what happened" required>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={kind === "mcq"
                        ? "What's wrong with this question? Be specific — which part of the statement, options or explanation needs review?"
                        : "What were you doing when this happened? What did you expect, and what actually appeared?"}
                      maxLength={4000}
                      data-testid="report-description"
                      style={{ ...inputStyle, height: "auto", minHeight: 120, padding: "12px 16px", lineHeight: 1.55, resize: "vertical" }}
                    />
                    <CharCount n={description.length} max={4000} />
                  </FieldGroup>

                  <FieldGroup label={`Attach screenshots (optional · up to ${MAX_ATTACHMENTS})`}>
                    <AttachmentDropzone
                      attachments={attachments}
                      onFiles={onFiles}
                      onRemove={removeAttachment}
                      disabled={attachments.length >= MAX_ATTACHMENTS || submitting}
                    />
                  </FieldGroup>

                  {err && (
                    <div
                      role="alert"
                      className="p-3 rounded-[12px] text-[12.5px] flex items-start gap-2"
                      style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.30)", color: "#991b1b" }}
                      data-testid="report-error"
                    >
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span className="flex-1">{err}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 md:px-6 py-4 border-t flex gap-2" style={{ borderColor: "rgba(11,11,13,0.06)", background: "#f8fafc" }}>
              {!done ? (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full text-[13px] font-semibold transition disabled:opacity-50"
                    style={{ background: "#ffffff", border: "1.5px solid rgba(11,11,13,0.12)", color: "#0b0b0d" }}
                    data-testid="report-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full text-[13px] font-semibold transition disabled:opacity-70"
                    style={{ background: "#0b0b0d", color: "#ffffff" }}
                    data-testid="report-submit"
                  >
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><Send size={14} /> Submit report</>}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-full text-[13px] font-semibold transition"
                  style={{ background: "#0b0b0d", color: "#ffffff" }}
                  data-testid="report-done-close"
                >
                  Close
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

const inputStyle = {
  width: "100%",
  height: 44,
  padding: "0 14px",
  borderRadius: 12,
  border: "1.5px solid rgba(11,11,13,0.12)",
  background: "#ffffff",
  color: "#0b0b0d",
  fontSize: 14,
  outline: "none",
  transition: "border-color .15s, box-shadow .15s",
};

function FieldGroup({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-[0.16em] mb-2" style={{ color: "#475569" }}>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function CharCount({ n, max }) {
  return (
    <div className="text-[10.5px] font-mono mt-1 text-right" style={{ color: "#94a3b8" }}>{n} / {max}</div>
  );
}

function McqPreviewCard({ preview }) {
  return (
    <div className="rounded-[12px] p-3.5 border" style={{ background: "rgba(220,38,38,0.04)", borderColor: "rgba(220,38,38,0.25)" }} data-testid="report-mcq-preview">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: "#dc2626" }}>
        Reporting this question
      </div>
      <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: "#0b0b0d", maxHeight: 90, overflow: "hidden" }}>
        {preview.statement}
      </div>
    </div>
  );
}

function AttachmentDropzone({ attachments, onFiles, onRemove, disabled }) {
  const [dragging, setDragging] = useState(false);
  const inputId = "report-attach-input";
  return (
    <div>
      <label
        htmlFor={inputId}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragging(false);
          if (!disabled && e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
        }}
        className="block rounded-[12px] border-2 border-dashed p-4 cursor-pointer transition-all text-center"
        style={{
          borderColor: dragging ? "#0284c7" : "rgba(11,11,13,0.15)",
          background: dragging ? "rgba(2,132,199,0.04)" : "#f8fafc",
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <ImageIcon size={20} className="mx-auto mb-1.5" style={{ color: "#64748b" }} />
        <div className="text-[12.5px]" style={{ color: "#334155" }}>
          Drop screenshots here or <span style={{ color: "#0284c7", textDecoration: "underline" }}>browse</span>
        </div>
        <div className="text-[10.5px] font-mono mt-1" style={{ color: "#94a3b8" }}>PNG · JPG · WebP · up to 4 MB each</div>
      </label>
      <input
        id={inputId} type="file" accept="image/*" multiple
        className="hidden" disabled={disabled}
        onChange={(e) => { if (e.target.files) onFiles(e.target.files); e.target.value = ""; }}
        data-testid="report-attach-input"
      />
      {attachments.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3" data-testid="report-attachments">
          {attachments.map((a) => (
            <div key={a.id} className="relative group rounded-[10px] overflow-hidden border" style={{ borderColor: "rgba(11,11,13,0.08)" }}>
              <img src={a.dataUrl} alt={a.name} className="w-full h-24 object-cover" />
              <button
                type="button"
                onClick={() => onRemove(a.id)}
                className="absolute top-1 right-1 w-7 h-7 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                style={{ background: "rgba(11,11,13,0.85)", color: "#ffffff" }}
                aria-label="Remove attachment"
                data-testid={`report-attach-remove-${a.id}`}
              >
                <Trash2 size={11} />
              </button>
              <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 text-[9.5px] font-mono truncate" style={{ background: "rgba(11,11,13,0.65)", color: "#ffffff" }}>
                {a.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionConfirmation({ report }) {
  return (
    <div className="text-center py-6" data-testid="report-success">
      <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
           style={{ background: "rgba(52,211,153,0.12)", border: "1.5px solid rgba(52,211,153,0.40)" }}>
        <CheckCircle2 size={30} style={{ color: "#059669" }} />
      </div>
      <h3 className="text-[17px] font-semibold" style={{ color: "#0b0b0d" }}>Report received</h3>
      <p className="text-[13px] mt-2 leading-relaxed max-w-sm mx-auto" style={{ color: "#475569" }}>
        Our team has been notified and will start reviewing shortly. You'll see updates inside the Student Portal — and we'll email you when it's resolved.
      </p>
      <div className="mt-4 text-[10.5px] font-mono" style={{ color: "#94a3b8" }}>
        Reference ID: {report.id.slice(0, 8).toUpperCase()}
      </div>
    </div>
  );
}
